import { useDispatch } from 'react-redux'
import { getAdditionalProps } from 'apps/studio/components/settings/inputs'
import { GCDSDropdown } from '@gocanvas/gcds/components'
import { isTrueStringMatch } from 'apps/builder/lib/utils'

export const SettingsSelect = ({
  id,
  label,
  property,
  computedProperty = () => property,
  choices = [],
  dynamicChoices = () => false,
  update,
  data,
  nestedProp,
  size,
  isDisabled = () => false,
  checkEntryValue = false,
  additionalProps = {},
  groupedOptions = false,
  portal = true,
  matchByAdditionalProperty, // other data properties that must match a given choice value
  additionalValueMatches = {}, // property values that should also match a given choice value
  labelAbove,
  className = '',
}) => {
  const dispatch = useDispatch()
  const options = dynamicChoices(data) || choices
  const lookupProperty = computedProperty({ data }) || property

  const {
    [lookupProperty]: value,
    [matchByAdditionalProperty]: additionalProperty,
  } = data

  const flatOptions = groupedOptions
    ? options.flatMap(({ options }) => options)
    : options

  const isOptionSelected = ({
    value: choiceValue,
    additionalProperties: choiceAdditionalProperties = {},
  }) => {
    let valueToCheck = value
    if (value && typeof value === 'object' && nestedProp) {
      valueToCheck = value[nestedProp]
    }

    let optionValue = choiceValue
    if (choiceValue && typeof choiceValue === 'object' && nestedProp) {
      optionValue = choiceValue[nestedProp]
    }

    const valueMatch =
      `${optionValue}` === `${additionalValueMatches[valueToCheck] !== undefined ? additionalValueMatches[valueToCheck] : valueToCheck}`

    if (matchByAdditionalProperty && additionalProperty) {
      return (
        valueMatch &&
        isTrueStringMatch(
          additionalProperty,
          choiceAdditionalProperties[matchByAdditionalProperty]
        )
      )
    } else if (checkEntryValue) {
      // checks current entry values against choice values which are arrays of values
      // this is used on initial load to match the current entry values to the correct choice
      const valuesToMatch = valueToCheck.map(v => {
        return { position: v.position, text: v.text }
      })
      const valuesMatch = valuesToMatch.every((v, i) => {
        return v.text === optionValue[i]?.text
      })
      return valuesMatch
    } else {
      return valueMatch
    }
  }

  const selected = flatOptions.find(isOptionSelected) || null
  const disabled = isDisabled(data) || !flatOptions.length

  const onChange = (args) => {
    if (!args) {
      return
    }
    
    const { value: updatedValue, label: updatedLabel } = args
    const selectedOption =
      flatOptions.find(
        ({ value, label }) =>
          String(value) === String(updatedValue) &&
          String(label) === String(updatedLabel)
      ) || {}
    if (!selectedOption) {
      return
    }
    const value =
      'realValue' in selectedOption
        ? selectedOption.realValue
        : selectedOption.value

    const updateProperty =
      computedProperty({ data, option: selectedOption }) || property

    dispatch(
      update({
        [updateProperty]: nestedProp
          ? {
              [nestedProp]: value,
              ...(selectedOption.additionalNestedProperties || {}),
            }
          : value,
        ...(selectedOption.additionalProperties || {}),
      })
    )
  }

  return (
    <GCDSDropdown
      options={flatOptions}
      inputType="secondary"
      label={label}
      size={size}
      value={selected}
      portal={portal}
      onChange={onChange}
      isOptionSelected={isOptionSelected}
      id={id}
      isDisabled={disabled}
      labelAbove={labelAbove}
      className={className}
      {...getAdditionalProps(additionalProps)}
    />
  )
}
